import { Song, Album, ShuffleMode, RepeatMode, STORAGE_KEYS } from './types';

// Helper functions for localStorage
export const saveToLocalStorage = (key: string, value: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving to localStorage (${key}):`, error);
  }
};

export const loadFromLocalStorage = (key: string, defaultValue: any) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : defaultValue;
  } catch (error) {
    console.error(`Error loading from localStorage (${key}):`, error);
    return defaultValue;
  }
};

// Format time from seconds to MM:SS
export const formatTime = (time: number) => {
  if (isNaN(time) || !isFinite(time)) return "0:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Generate a shuffled queue based on shuffle mode
export const generateShuffledQueue = (
  mode: ShuffleMode, 
  currentSong: Song | null, 
  currentAlbum: Album | null, 
  albums: Album[]
): Song[] => {
  let songsToShuffle: Song[] = [];
  
  if (mode === 'album' && currentAlbum?.songs) {
    // Shuffle only current album songs
    songsToShuffle = [...currentAlbum.songs];
  } else if (mode === 'all') {
    // Shuffle all songs across all albums
    songsToShuffle = albums.flatMap(album => album.songs || []);
  }
  
  // Fisher-Yates shuffle algorithm
  for (let i = songsToShuffle.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [songsToShuffle[i], songsToShuffle[j]] = [songsToShuffle[j], songsToShuffle[i]];
  }
  
  // If current song exists, move it to the start of the queue
  if (currentSong) {
    const currentIndex = songsToShuffle.findIndex(song => song.id === currentSong.id);
    if (currentIndex > -1) {
      const current = songsToShuffle.splice(currentIndex, 1)[0];
      songsToShuffle.unshift(current);
    }
  }
  
  return songsToShuffle;
};

// Get the next song based on repeat mode, shuffle mode, etc.
export const getNextSong = (
  currentSong: Song | null, 
  repeatMode: RepeatMode,
  shuffleMode: ShuffleMode,
  shuffledQueue: Song[],
  currentAlbum: Album | null
): Song | null => {
  // Add debug logging
  // console.log(`Getting next song. Current song: ${currentSong?.name}, Repeat: ${repeatMode}, Shuffle: ${shuffleMode}`);
  
  if (!currentSong) return null;
  
  // If repeat song is enabled, play the same song again
  if (repeatMode === 'song') {
    // console.log('Repeat song mode active, returning current song');
    return currentSong;
  }
  
  try {
    // If shuffle is enabled, get next song from the shuffled queue
    if (shuffleMode !== 'none' && shuffledQueue.length > 0) {
      // console.log(`Using shuffled queue with ${shuffledQueue.length} songs`);
      const currentIndex = shuffledQueue.findIndex(song => song.id === currentSong.id);
      // console.log(`Current index in shuffle queue: ${currentIndex}`);
      
      if (currentIndex > -1 && currentIndex < shuffledQueue.length - 1) {
        // Next song in shuffled queue
        return shuffledQueue[currentIndex + 1];
      } else if (repeatMode === 'album') {
        // If we're at the end of the queue and repeat is enabled, return to the start
        // console.log('End of shuffled queue, returning to start due to repeat album');
        return shuffledQueue[0];
      }
    } else if (currentAlbum?.songs && currentAlbum.songs.length > 0) {
      // Normal sequential playback
      // console.log(`Using sequential playback with ${currentAlbum.songs.length} songs`);
      
      // Sort songs by track number if available
      const sortedSongs = [...currentAlbum.songs].sort((a, b) => {
        if (a.trackNumber !== undefined && b.trackNumber !== undefined) {
          return a.trackNumber - b.trackNumber;
        }
        if (a.trackNumber !== undefined) return -1;
        if (b.trackNumber !== undefined) return 1;
        return 0;
      });
      
      const currentIndex = sortedSongs.findIndex(song => song.id === currentSong.id);
      // console.log(`Current index in album: ${currentIndex}`);
      
      if (currentIndex > -1 && currentIndex < sortedSongs.length - 1) {
        // Next song in album
        return sortedSongs[currentIndex + 1];
      } else if (repeatMode === 'album') {
        // If at end of album and album repeat is on, go back to first song
        // console.log('End of album, returning to first song due to repeat album');
        return sortedSongs[0];
      }
    }
    
    // If we get here, no next song was found
    // console.log('No next song found');
    return null;
  } catch (error) {
    // console.error('Error in getNextSong:', error);
    return null;
  }
};

// Get the previous song based on repeat mode, shuffle mode, etc.
export const getPreviousSong = (
  currentSong: Song | null, 
  repeatMode: RepeatMode,
  shuffleMode: ShuffleMode,
  shuffledQueue: Song[],
  currentAlbum: Album | null
): Song | null => {
  // Add debug logging
  // console.log(`Getting previous song. Current song: ${currentSong?.name}, Repeat: ${repeatMode}, Shuffle: ${shuffleMode}`);
  
  if (!currentSong) return null;
  
  // If repeat song is enabled, play the same song again
  if (repeatMode === 'song') {
    // console.log('Repeat song mode active, returning current song');
    return currentSong;
  }
  
  try {
    // If shuffle is enabled, get previous song from the shuffled queue
    if (shuffleMode !== 'none' && shuffledQueue.length > 0) {
      // console.log(`Using shuffled queue with ${shuffledQueue.length} songs`);
      const currentIndex = shuffledQueue.findIndex(song => song.id === currentSong.id);
      // console.log(`Current index in shuffle queue: ${currentIndex}`);
      
      if (currentIndex > 0) {
        // Previous song in shuffled queue
        return shuffledQueue[currentIndex - 1];
      } else if (repeatMode === 'album') {
        // If we're at the start of the queue and repeat is enabled, go to the end
        //      console.log('Start of shuffled queue, going to end due to repeat album');
        return shuffledQueue[shuffledQueue.length - 1];
      }
    } else if (currentAlbum?.songs && currentAlbum.songs.length > 0) {
      // Normal sequential playback
      // console.log(`Using sequential playback with ${currentAlbum.songs.length} songs`);
      
      // Sort songs by track number if available
      const sortedSongs = [...currentAlbum.songs].sort((a, b) => {
        if (a.trackNumber !== undefined && b.trackNumber !== undefined) {
          return a.trackNumber - b.trackNumber;
        }
        if (a.trackNumber !== undefined) return -1;
        if (b.trackNumber !== undefined) return 1;
        return 0;
      });
      
      const currentIndex = sortedSongs.findIndex(song => song.id === currentSong.id);
      // console.log(`Current index in album: ${currentIndex}`);
      
      if (currentIndex > 0) {
        // Previous song in album
        return sortedSongs[currentIndex - 1];
      } else if (repeatMode === 'album') {
        // If at start of album and album repeat is on, go to last song
        // console.log('Start of album, going to last song due to repeat album');
        return sortedSongs[sortedSongs.length - 1];
      }
    }
    
    // If we get here, no previous song was found
    // console.log('No previous song found');
    return null;
  } catch (error) {
    console.error('Error in getPreviousSong:', error);
    return null;
  }
}; 